// Development fixture for the Energy tab (enabled with `?mock=energy`). Generates a plausible plan snapshot
// shaped like a January weekday (cheap night, 09-12 and 16-20 peaks), anchored at the current time so the
// "now" line and the relative timestamps look live. It exercises every element of the tab: actions switching
// mid-hour, a discharge with grid export (the next 17:00-18:00), a negative-price no-export block tomorrow
// 11:45-13:30, month-to-date cost, and a live power state.
import {
  EnergyActualSlot,
  EnergyAirQualityRoom,
  EnergyPlanSlot,
  EnergyPlanSnapshot,
  EnergyPowerSeries,
  SolarState,
  VentLevel,
} from '../../common';
import { tallinnParts } from './tallinn_time';

const SLOT_MS = 15 * 60_000;
const POWER_MS = 5 * 60_000;
const BATTERY_KWH = 17.28;
const FLOOR_PERCENT = 13;
const CHARGE_W = 6000;

function spotFor(hour: number, minute: number, dayIndex: number, negativeNoon: boolean): number {
  const h = hour + minute / 60;
  if (negativeNoon && h >= 11.75 && h < 13.5) {
    return Math.round((-2.4 + Math.sin(h * 9) * 0.6) * 100) / 100; // windy, sunny noon: below zero
  }
  // Rough January weekday shape in c/kWh excl VAT.
  const base =
    h < 5
      ? 4.5 - Math.sin((h / 5) * Math.PI) * 1.5
      : h < 8
        ? 5 + (h - 5) * 5
        : h < 12
          ? 21 + Math.sin(((h - 8) / 4) * Math.PI) * 5
          : h < 16
            ? 12 - Math.sin(((h - 12) / 4) * Math.PI) * 3
            : h < 20
              ? 24 + Math.sin(((h - 16) / 4) * Math.PI) * 9
              : 20 - (h - 20) * 3.6;
  const wiggle = Math.sin(h * 2.7 + dayIndex * 1.3) * 1.2 + Math.cos(h * 5.1 + dayIndex) * 0.6;
  return Math.round((base + wiggle + dayIndex * 1.5) * 100) / 100;
}

function networkFor(hour: number): number {
  if (hour < 7 || hour >= 22) {
    return 3.03;
  }
  if ((hour >= 9 && hour < 12) || (hour >= 16 && hour < 20)) {
    return 8.18; // Nov-Mar weekday peak
  }
  return 5.29;
}

const importPriceFor = (spot: number, hour: number) =>
  Math.round((spot * 1.24 + 0.47 + 1.24 * (networkFor(hour) + 0.84 + 0.758 + 0.21 + 0.373)) * 100) / 100;

function actionFor(hour: number, minute: number, dischargeDay: boolean): EnergyPlanSlot['action'] {
  const h = hour + minute / 60;
  if (h >= 2.25 && h < 4.75) {
    return 'charge';
  }
  if (dischargeDay && h >= 17 && h < 18) {
    return 'discharge';
  }
  if ((h >= 5 && h < 8.75) || (h >= 12.25 && h < 15.75)) {
    return 'hold';
  }
  return 'auto';
}

const dateKey = (t: number) => {
  const p = tallinnParts(t);
  return `${p.year}-${p.month}-${p.day}`;
};

function ventFor(hour: number): VentLevel {
  if ((hour >= 9 && hour < 12) || (hour >= 16 && hour < 20)) {
    return 'low';
  }
  if (hour >= 1 && hour < 5) {
    return 'high'; // cheap night: flush air while heat is cheap
  }
  return 'middle';
}

function loadFor(hour: number, minute: number, temp: number): number {
  const h = hour + minute / 60;
  const heat = Math.max(0, 16 - temp) * 0.012; // heat pump + preheater share per slot
  const morning = Math.exp(-((h - 7.5) ** 2) / 2) * 0.25;
  const evening = Math.exp(-((h - 19) ** 2) / 4) * 0.35;
  return 0.22 + heat + morning + evening;
}

function pvFor(hour: number, minute: number, dayIndex: number): number {
  const h = hour + minute / 60;
  if (h < 9.5 || h > 15) {
    return 0;
  }
  const clear = dayIndex === 1 ? 0.5 : 1;
  return Math.round(Math.sin(((h - 9.5) / 5.5) * Math.PI) * 0.22 * clear * 1000) / 1000;
}

// CO2 ppm: the study fills up during the working day, the kids' bedroom overnight (peaking above 1200 ppm
// before the morning, so the badge and the threshold lines have something to show).
function co2For(room: string, hour: number, minute: number): number {
  const h = hour + minute / 60;
  if (room === 'Kabinet') {
    return Math.round(h >= 9 && h < 18 ? 560 + 520 * Math.sin(((h - 9) / 9) * Math.PI) : 520);
  }
  const sinceBedtime = (h - 20.5 + 24) % 24;
  return Math.round(sinceBedtime < 11 ? 640 + 640 * Math.sin((sinceBedtime / 11) * Math.PI * 0.9) : 600);
}

interface SimResult {
  socEnd: number;
  gridKwh: number;
}

function simulate(action: EnergyPlanSlot['action'], soc: number, load: number, pv: number, powerW = 0): SimResult {
  let batteryKwh = (soc / 100) * BATTERY_KWH;
  const net = load - pv;
  let grid: number;
  if (action === 'charge') {
    const charge = Math.min((powerW / 1000) * 0.25, BATTERY_KWH - batteryKwh);
    batteryKwh += charge * 0.95;
    grid = net + charge;
  } else if (action === 'discharge') {
    const out = Math.min((powerW / 1000) * 0.25, batteryKwh - (FLOOR_PERCENT / 100) * BATTERY_KWH);
    batteryKwh -= out;
    grid = net - out;
  } else if (action === 'hold') {
    const surplus = Math.max(0, -net);
    const stored = Math.min(surplus, BATTERY_KWH - batteryKwh);
    batteryKwh += stored;
    grid = Math.max(0, net) - (surplus - stored);
  } else {
    if (net >= 0) {
      const available = Math.max(0, batteryKwh - (FLOOR_PERCENT / 100) * BATTERY_KWH);
      const used = Math.min(net, available);
      batteryKwh -= used;
      grid = net - used;
    } else {
      const stored = Math.min(-net, BATTERY_KWH - batteryKwh);
      batteryKwh += stored;
      grid = net + stored;
    }
  }
  return { socEnd: Math.round((batteryKwh / BATTERY_KWH) * 1000) / 10, gridKwh: Math.round(grid * 1000) / 1000 };
}

export function energyMockSnapshot(now: number = Date.now()): EnergyPlanSnapshot {
  const current = Math.floor(now / SLOT_MS) * SLOT_MS;
  const start = current - 24 * 3_600_000;
  const end = current + 36 * 3_600_000;
  const nowParts = tallinnParts(now);
  const today = dateKey(now);
  const tomorrow = dateKey(now + 24 * 3_600_000);
  // The next 17:00-18:00 (today until it has ended).
  const dischargeDay = nowParts.hour < 18 ? today : tomorrow;
  // Day-ahead prices arrive ~14:00 local; later slots carry estimated prices.
  let midnightsLeft = nowParts.hour >= 14 ? 2 : 1;
  let knownUntil = current + SLOT_MS;
  for (; knownUntil < end; knownUntil += SLOT_MS) {
    const p = tallinnParts(knownUntil);
    if (p.hour === 0 && p.minute === 0 && --midnightsLeft === 0) {
      break;
    }
  }

  const actual: EnergyActualSlot[] = [];
  const power: EnergyPowerSeries = {
    start,
    stepMs: POWER_MS,
    loadW: [],
    pvW: [],
    gridW: [],
    batteryW: [],
    socPercent: [],
  };
  const slots: EnergyPlanSlot[] = [];
  let soc = 38;
  let selfUseSoc = 0;
  let selfUseCost = 0;
  let totalCost = 0;
  let dayIndex = 0;
  let lastDay = tallinnParts(start).day;
  for (let t = start; t < end; t += SLOT_MS) {
    const p = tallinnParts(t);
    if (p.day !== lastDay) {
      dayIndex++;
      lastDay = p.day;
    }
    const temp = Math.round((-7 + Math.sin(((p.hour - 9) / 24) * 2 * Math.PI) * 3 - dayIndex * 0.8) * 10) / 10;
    const day = dateKey(t);
    const spot = spotFor(p.hour, p.minute, dayIndex, day === tomorrow);
    const importPrice = importPriceFor(spot, p.hour);
    const exportPrice = Math.round((spot - 1.96) * 100) / 100;
    const curtailExport = exportPrice < 0;
    const action = actionFor(p.hour, p.minute, day === dischargeDay);
    const ventLevel = ventFor(p.hour);
    const batteryPowerW = action === 'charge' || action === 'discharge' ? CHARGE_W : undefined;
    if (t < current) {
      const noise = 1 + Math.sin(t / 1_700_000) * 0.15 + Math.cos(t / 900_000) * 0.08;
      // 5-minute powers (kW): the heat pump compressor runs 2.4 kW for 10 minutes in every 45, on top of the rest.
      const meanKw = loadFor(p.hour, p.minute, temp) * noise * 4;
      const loadKw = [0, 1, 2].map((j) => {
        const on = Math.floor((t + j * POWER_MS) / POWER_MS) % 9 < 2;
        return Math.max(0.15, meanKw - 0.53) + (on ? 2.4 : 0);
      });
      const pvMeanKw = pvFor(p.hour, p.minute, dayIndex) * (0.8 + Math.abs(Math.sin(t / 2_000_000)) * 0.4) * 4;
      const wiggle = Math.sin(t / 1_000_000) * 0.15;
      const pvKw = [1 + wiggle, 1, 1 - wiggle].map((f) => pvMeanKw * f);
      const load = Math.round(((loadKw[0] + loadKw[1] + loadKw[2]) / 12) * 1000) / 1000;
      const pv = Math.round((pvMeanKw / 4) * 1000) / 1000;
      const r = simulate(action, soc, load, pv, batteryPowerW);
      // In Self Use the battery takes the swings; otherwise the grid does.
      const gridMeanKw = r.gridKwh * 4;
      const netMeanKw = (load - pv) * 4;
      for (let j = 0; j < 3; j++) {
        const gridKw = action === 'auto' && r.gridKwh === 0 ? 0 : gridMeanKw + loadKw[j] - pvKw[j] - netMeanKw;
        power.loadW.push(Math.round(loadKw[j] * 1000));
        power.pvW.push(Math.round(pvKw[j] * 1000));
        power.gridW.push(Math.round(gridKw * 1000));
        power.batteryW.push(Math.round((pvKw[j] + gridKw - loadKw[j]) * 1000));
        power.socPercent.push(Math.round((soc + ((r.socEnd - soc) * (j + 1)) / 3) * 10) / 10);
      }
      soc = r.socEnd;
      actual.push({
        start: t,
        socEndPercent: r.socEnd,
        gridKwh: r.gridKwh,
        pvKwh: pv,
        loadKwh: load,
        spot,
        importPrice,
        exportPrice,
        action,
        ventLevel,
      });
      continue;
    }
    if (t === current) {
      selfUseSoc = soc;
    }
    const load = Math.round(loadFor(p.hour, p.minute, temp) * 1000) / 1000;
    const pv = pvFor(p.hour, p.minute, dayIndex);
    const r = simulate(action, soc, load, pv, batteryPowerW);
    const costCents = Math.round(r.gridKwh * (r.gridKwh >= 0 ? importPrice : exportPrice) * 100) / 100;
    totalCost += costCents;
    const su = simulate('auto', selfUseSoc, load, pv);
    selfUseSoc = su.socEnd;
    selfUseCost += su.gridKwh * (su.gridKwh >= 0 ? importPrice : exportPrice);
    slots.push({
      start: t,
      action,
      batteryPowerW,
      curtailExport,
      socStartPercent: soc,
      socEndPercent: r.socEnd,
      gridKwh: r.gridKwh,
      costCents,
      spot,
      importPrice,
      exportPrice,
      estimatedPrice: t >= knownUntil,
      loadKwh: load,
      pvKwh: pv,
      outdoorTempC: temp,
      ventLevel,
    });
    soc = r.socEnd;
  }

  const rooms: EnergyAirQualityRoom[] = ['Kabinet', 'Magamistuba'].map((name) => {
    const slots: EnergyAirQualityRoom['slots'] = [];
    for (let t = start; t <= current; t += SLOT_MS) {
      const p = tallinnParts(t);
      const co2 = co2For(name, p.hour, p.minute + 7);
      slots.push({ start: t, co2, co2Max: co2 + 25 });
    }
    const p = tallinnParts(now - 2 * 60_000);
    return {
      name,
      latest: {
        timestamp: now - 2 * 60_000,
        co2: co2For(name, p.hour, p.minute),
        temperatureC: name === 'Kabinet' ? 22.4 : 21.1,
        humidity: name === 'Kabinet' ? 38 : 44,
        pm25: 4,
        tvoc: name === 'Kabinet' ? 35 : 80,
      },
      slots,
    };
  });

  const dayOfMonth = nowParts.day;
  return {
    createdAt: now - 4 * 60_000,
    mode: 'shadow',
    ventilationMode: 'shadow',
    socPercent: slots[0]?.socStartPercent ?? soc,
    socFloorPercent: FLOOR_PERCENT,
    batteryNominalKwh: BATTERY_KWH,
    totalCostCents: Math.round(totalCost),
    selfUseCostCents: Math.round(selfUseCost),
    slots,
    actual,
    power,
    chargeWindows: {
      gridChargeEnabled: true,
      targetSocPercent: 90,
      windows: [{ start: '02:00', end: '05:00' }],
    },
    alerts: [
      {
        timestamp: now - 42 * 60_000,
        kind: 'mock',
        message: 'Mock data (?mock=energy): not a real plan',
      },
      {
        timestamp: now - 5 * 3_600_000,
        kind: 'failsafe',
        message: 'fail-safe: modbus-error (connect ENETUNREACH 192.168.1.127:502)',
        resolvedAt: now - 5 * 3_600_000 + 4 * 60_000,
      },
      {
        timestamp: now - 3 * 60_000,
        kind: 'prices',
        message: 'Day-ahead prices for tomorrow not published yet; using estimates',
      },
    ],
    yesterday: {
      date: 'yesterday',
      actualCostCents: 1372,
      gridCostCents: 1340,
      savingVsSelfUseCents: 210,
      savingVsOldScheduleCents: 18,
      loadMaeKwh: 0.12,
      pvMaeKwh: 0.02,
      failsafeMinutes: 0,
      completeness: 0.99,
    },
    ventManualOverrideSince: now - 20 * 60_000,
    configVersion: 'mock0a1b2c3d',
    airQuality: { thresholds: { good: 1000, high: 1200 }, rooms },
    monthToDate: {
      month: `${nowParts.year}-${String(nowParts.month).padStart(2, '0')}`,
      costCents: dayOfMonth * 1349 - 403,
      savingVsSelfUseCents: dayOfMonth * 187 - 4,
      daysCovered: dayOfMonth,
      daysInPeriod: dayOfMonth,
      completeness: 0.996,
    },
  };
}

// The live power state the Pi would post (/solar/state): the current plan slot's means, with some wobble.
export function energyMockSolarState(plan: EnergyPlanSnapshot, now: number): SolarState | undefined {
  const s = plan.slots.find((x) => x.start <= now && now < x.start + SLOT_MS) ?? plan.slots[0];
  if (!s) {
    return undefined;
  }
  const wobble = 1 + Math.sin(now / 37_000) * 0.06;
  const load = s.loadKwh * 4000 * wobble;
  const pv = s.pvKwh * 4000;
  const grid = s.gridKwh * 4000 + (load - s.loadKwh * 4000);
  return {
    consumePowerWatts: Math.round(load),
    solarProductionWatts: Math.round(pv),
    feedInPowerWatts: Math.round(-grid),
    batteryPowerWatts: Math.round(grid + pv - load),
    batteryPercentage: Math.round(s.socStartPercent),
    receivedAt: now - 4_000,
  };
}

// What an older controller sends: none of the optional fields the redesigned tab added.
export function legacySnapshot(s: EnergyPlanSnapshot): EnergyPlanSnapshot {
  const out: EnergyPlanSnapshot = { ...s, yesterday: s.yesterday && { ...s.yesterday } };
  delete out.power;
  delete out.airQuality;
  delete out.monthToDate;
  delete out.batteryNominalKwh;
  delete out.socFloorPercent;
  delete out.yesterday?.gridCostCents;
  return out;
}
